/* eslint-disable @typescript-eslint/no-explicit-any */
import { ActionWithPayload, F1, SMap } from '@client/new/helpers/types';
import {
  SubExerciseDTO,
  HIITExerciseDTO,
  TabataExerciseDTO,
  ParentExerciseDTO,
  ExerciseDTO,
} from '../model/workout.domain';
import { scrollToElement } from './form.utils';
import {
  isParentExercise,
  toHIITExercise,
  toTabataExercise,
  createHIIT,
  createTabata,
  toParentExercise,
  toExerciseIds,
  toExerciseMap,
  appendExercises,
  toSet,
} from './workout.mappers';
import {
  defaultHIITSettings,
  defaultTabataSettings,
  Exercise,
  ExerciseSettings,
  ParentExercise,
  SubExercise,
  Workout,
} from './workouts.domain';
import { DelegatedActionMap } from '../../workout/models/workoutEdit.model';
import { EditWorkoutVMDispatcher } from '../../workout/view-models/workoutEdit.viewModel';
import { omitObject } from '@client/new/helpers/general';
import { UseFormReturn } from 'react-hook-form';
import { defaults } from './workout.schema';
import { FormMapType } from '@client/new/helpers/form/useFormMap';

export const getSettings = (vm: Workout, id: string): ExerciseSettings => {
  const parentId = getParentId(vm, id);
  const stored = vm.exerciseSettingsMap[parentId];
  const parent = vm.exerciseMap[parentId];
  if (stored) return stored;
  if (!parent || !isParentExercise(parent)) return defaultTabataSettings;

  const firstExercise = parent.value.exercises?.[0];
  const firstSet = firstExercise?.value?.sets?.[0];
  const isHIIT = parent.type === 'HIIT';
  if (!firstSet) return isHIIT ? defaultHIITSettings : defaultTabataSettings;
  const { rest, time } = firstSet;
  const sets = isHIIT ? undefined : firstExercise.value.sets.length;
  return { type: parent.type, id: parentId, rest, time, sets };
};

export type VMCallback = F1<Workout, Workout>;
type SideEffect = ActionWithPayload<'scrollToElement', string>;

const _sideEffect = (a: SideEffect) => {
  switch (a.type) {
    case 'scrollToElement':
      scrollToElement(a.payload);
  }
};

let sideEffect = _sideEffect;

export const setSideEffectConsumer = (fn: typeof _sideEffect) =>
  (sideEffect = fn);

export const getEmptyWorkout = (): Workout => ({
  exerciseSettingsMap: {},
  exerciseMap: {},
  exerciseIds: [],
});

export const toWorkout = (exercises: ExerciseDTO[]): Workout => {
  const ps = exercises.map(toParentExercise);
  const exerciseMap = toExerciseMap(ps);
  const exerciseIds = toExerciseIds(ps);
  const res: Workout = { exerciseMap, exerciseIds, exerciseSettingsMap: {} };
  res.exerciseSettingsMap = exerciseIds.reduce(
    (acc, { id }) => ({ ...acc, [id]: getSettings(res, id) }),
    res.exerciseSettingsMap,
  );
  return res;
};

const getSets = <T extends Exercise>(
  v: T,
  parent: ParentExercise,
  settings: ExerciseSettings,
): TabataExerciseDTO['sets'] => {
  if (isParentExercise(v)) return [];
  const n = (parent.type === 'HIIT' ? parent.value.rounds : settings.sets) || 1;
  if (n === v.value.sets.length) return v.value.sets;
  if (n < v.value.sets.length) return v.value.sets.slice(0, n);
  const sets = new Array(n - v.value.sets.length).fill(toSet(settings));
  return [...v.value.sets, ...sets];
};
export const applySettingsToOne = <T extends Exercise>(
  v: T,
  parent: ParentExercise,
  settings: ExerciseSettings,
): T => {
  if (isParentExercise(v)) return v;
  const sets = getSets(v, parent, settings).map((s) => ({
    ...s,
    time: settings.time,
    rest: settings.rest,
  }));
  return { ...v, value: { ...v.value, sets } as any as T };
};

export const applySettings =
  (settings: ExerciseSettings, formVM: SMap<UseFormReturn>): VMCallback =>
  (vm) => {
    const parent = vm.exerciseMap[settings.id];
    if (!isParentExercise(parent)) return vm;
    const ids = vm.exerciseIds.find((e) => e.id === settings.id)?.items || [];
    const exerciseMap = ids.reduce(
      (acc, { id }) => {
        const nextExercise = applySettingsToOne(
          vm.exerciseMap[id],
          parent,
          settings,
        );
        formVM[id]?.reset(nextExercise.value);
        return { ...acc, [id]: nextExercise };
      },
      { ...vm.exerciseMap },
    );
    const exerciseSettingsMap = {
      ...vm.exerciseSettingsMap,
      [settings.id]: settings,
    };
    return { ...vm, exerciseMap, exerciseSettingsMap };
  };

export const applySetsCountToOne = <T extends Exercise>(
  v: T,
  parent: ParentExercise,
  settings: ExerciseSettings,
): T => {
  if (isParentExercise(v)) return v;
  const sets = getSets(v, parent, settings);
  return { ...v, value: { ...v.value, sets } } as T;
};

export const applySetsCount =
  (parentId: string, formVM: SMap<UseFormReturn>): VMCallback =>
  (vm) => {
    const parent = vm.exerciseMap[parentId];
    const settings = getSettings(vm, parentId);
    if (!isParentExercise(parent)) return vm;

    const ids = vm.exerciseIds.find((e) => e.id === settings.id)?.items || [];

    const exerciseMap = ids.reduce(
      (acc, { id }) => {
        const nextExercise = applySetsCountToOne(
          vm.exerciseMap[id],
          parent,
          settings,
        );

        formVM[id]?.reset(nextExercise.value);
        return { ...acc, [id]: nextExercise };
      },
      { ...vm.exerciseMap },
    );
    const exerciseSettingsMap = {
      ...vm.exerciseSettingsMap,
      [settings.id]: settings,
    };

    return { ...vm, exerciseMap, exerciseSettingsMap };
  };

export const reorder =
  (draggedExerciseIds: Workout['exerciseIds']): VMCallback =>
  (vm) => {
    if (draggedExerciseIds.length !== 1) return vm; // we support only one parent exercise
    const parent = draggedExerciseIds[0];
    const exerciseIds = vm.exerciseIds.map((e) =>
      e.id !== parent.id ? e : parent,
    );
    return { ...vm, exerciseIds };
  };

const toSubExercise = (p: {
  delta?: Partial<SubExerciseDTO>;
  parent: ParentExercise;
  settings: ExerciseSettings;
}) => {
  const exercise =
    p.parent.type === 'HIIT'
      ? toHIITExercise(p.delta as Partial<HIITExerciseDTO>)
      : toTabataExercise(p.delta as Partial<TabataExerciseDTO>);
  return applySettingsToOne(exercise, p.parent, p.settings);
};

const recalculateAfterAddingSubExercise =
  (parent: ParentExercise, exercises: SubExercise[]): VMCallback =>
  (vm) => {
    const nextParent = appendExercises(parent, exercises);
    const exerciseMap = {
      ...vm.exerciseMap,
      [parent.value.id]: nextParent,
    };
    exercises.forEach((e) => (exerciseMap[e.value.id] = e));
    const newItems = exercises.map((e) => ({ id: e.value.id }));
    const exerciseIds = vm.exerciseIds.map((e) =>
      e.id === parent.value.id ? { ...e, items: [...e.items, ...newItems] } : e,
    );
    return { ...vm, exerciseMap, exerciseIds };
  };

export const addSubExercise =
  (parentId: string, delta?: Partial<SubExerciseDTO>): VMCallback =>
  (vm) => {
    const parent = vm.exerciseMap[parentId];
    if (!isParentExercise(parent)) return vm;
    const settings = getSettings(vm, parentId);
    const newExercise = toSubExercise({ delta, parent, settings });
    sideEffect({ type: 'scrollToElement', payload: newExercise.value.id });
    return recalculateAfterAddingSubExercise(parent, [newExercise])(vm);
  };

export const addSubExercises =
  (p: DelegatedActionMap['addSubExercises']): VMCallback =>
  (vm) => {
    const parent = vm.exerciseMap[p.parentId];
    if (!isParentExercise(parent) || p.media.length === 0) return vm;
    const settings = getSettings(vm, p.parentId);
    const exercises = p.media.map((m) =>
      toSubExercise({
        delta: {
          media: omitObject((m || {}) as any, ['title', 'description']),
          name: m.title,
          description: m.description || '',
        },
        parent,
        settings,
      }),
    );
    sideEffect({ type: 'scrollToElement', payload: exercises[0].value.id });
    return recalculateAfterAddingSubExercise(parent, exercises)(vm);
  };

export const addAutoplayed =
  (
    settings: ExerciseSettings,
    fwdDispatch?: EditWorkoutVMDispatcher,
  ): VMCallback =>
  (vm) => {
    const exerciseSettingsMap = {
      ...vm.exerciseSettingsMap,
      [settings.id]: settings,
    };
    const dtoBase: ParentExerciseDTO = {
      description: '',
      name: settings.type === 'HIIT' ? 'HIIT' : 'Tabata',
      rounds: settings.rounds || defaults.rounds.default,
      longRest: 0,
      exercises: [],
      id: settings.id,
    };
    const parent =
      settings.type === 'HIIT' ? createHIIT(dtoBase) : createTabata(dtoBase);
    const exerciseMap = { ...vm.exerciseMap, [settings.id]: parent };
    const exerciseIds = [...vm.exerciseIds, { id: settings.id, items: [] }];
    sideEffect({ type: 'scrollToElement', payload: settings.id });
    fwdDispatch?.('updateAutoplayedSettings', exerciseSettingsMap);
    return {
      exerciseIds,
      exerciseMap,
      exerciseSettingsMap,
    };
  };
export const getParentId = (vm: Workout, id: string) => {
  if (isParentExercise(vm.exerciseMap[id])) return id;
  const parent = vm.exerciseIds.find((p) => p.items.find((c) => c.id === id));
  if (!parent) throw new Error('Parent not found');
  return parent.id;
};

export const removeSubExercise =
  (id: string, form: Omit<FormMapType, 'formMap'>): VMCallback =>
  (vm) => {
    if (isParentExercise(vm.exerciseMap[id])) return vm;
    const parentId = getParentId(vm, id);
    const parent = vm.exerciseMap[parentId] as ParentExercise;
    const nextParent = {
      ...parent,
      value: {
        ...parent.value,
        exercises: parent.value.exercises.filter((e) => e.value.id !== id),
      },
    } as ParentExercise;
    const exerciseIds = vm.exerciseIds.map((p) =>
      p.id === parentId
        ? { ...p, items: p.items.filter((e) => e.id !== id) }
        : p,
    );
    const exerciseMap = { ...vm.exerciseMap, [parentId]: nextParent };
    delete exerciseMap[id];
    form.removeForm(id);

    return {
      ...vm,
      exerciseIds,
      exerciseMap,
    };
  };

export const removeExercise =
  (id: string, form: Omit<FormMapType, 'formMap'>): VMCallback =>
  (vm) => {
    if (!isParentExercise(vm.exerciseMap[id])) return vm;

    const childIds =
      vm.exerciseIds.find((p) => p.id === id)?.items.map((c) => c.id) || [];
    const exerciseMap = omitObject(vm.exerciseMap, [id, ...childIds]);
    const exerciseIds = vm.exerciseIds.filter((p) => p.id !== id);

    [id, ...childIds].forEach((c) => form.removeForm(c));
    return { ...vm, exerciseMap, exerciseIds };
  };
